import { useState, useEffect, useRef } from "react";
import API from "../../../../config/api";
import { useTranslation } from "../../../../i18n/LanguageProvider";
import "./Filters.sass";

export default function Filters({ onFiltersChange }) {
    const { language, t } = useTranslation();
    const [search, setSearch] = useState("");
    const [categories, setCategories] = useState([]);
    const [places, setPlaces] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedPlaces, setSelectedPlaces] = useState([]);
    const [categoriesOpen, setCategoriesOpen] = useState(false);
    const [placesOpen, setPlacesOpen] = useState(false);

    const categoriesRef = useRef(null);
    const placesRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (categoriesRef.current && !categoriesRef.current.contains(event.target)) {
                setCategoriesOpen(false);
            }
            if (placesRef.current && !placesRef.current.contains(event.target)) {
                setPlacesOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        // Fetch categories and places
        Promise.all([
            fetch(`${API.getWoman}?categories=true&lang=${language}`, {
                credentials: "include",
            }).then((res) => res.json()),
            fetch(`${API.getWoman}?places=true&lang=${language}`, {
                credentials: "include",
            }).then((res) => res.json()),
        ])
            .then(([categoriesData, placesData]) => {
                setCategories(categoriesData);
                setPlaces(placesData);
            })
            .catch((err) => console.error("Error fetching filter data:", err));
    }, [language]);

    const handleCategoryToggle = (categoryId) => {
        setSelectedCategories((prev) =>
            prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId],
        );
    };

    const handlePlaceToggle = (placeId) => {
        setSelectedPlaces((prev) =>
            prev.includes(placeId) ? prev.filter((id) => id !== placeId) : [...prev, placeId],
        );
    };

    const handleApplyFilters = () => {
        const hasAnyFilter = search.trim() || selectedCategories.length > 0 || selectedPlaces.length > 0;

        if (!hasAnyFilter) return;

        onFiltersChange({
            search: search.trim(),
            categoryIds: selectedCategories,
            placeIds: selectedPlaces,
        });
    };

    const handleResetFilters = () => {
        setSearch("");
        setSelectedCategories([]);
        setSelectedPlaces([]);
        onFiltersChange(null);
    };

    const hasFilters = search.trim() || selectedCategories.length > 0 || selectedPlaces.length > 0;

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleApplyFilters();
        }
    };

    return (
        <div id="filters" className="filters card-common">
            <h3 className="filters__title">{t("filters.title")}</h3>
            <div className="filters__grid">
                <div className="filters__field">
                    <label>{t("filters.name")}</label>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={t("filters.namePlaceholder")}
                    />
                </div>
                <div className="filters__field">
                    <label>{t("filters.category")}</label>
                    <div className="filters__multiselect" ref={categoriesRef}>
                        <div className="filters__multiselect-header" onClick={() => setCategoriesOpen(!categoriesOpen)}>
                            <span>
                                {selectedCategories.length > 0
                                    ? t("filters.selected", { count: selectedCategories.length })
                                    : t("filters.categoriesPlaceholder")}
                            </span>
                            <span className={`arrow ${categoriesOpen ? "open" : ""}`}>▼</span>
                        </div>
                        {categoriesOpen && (
                            <div className="filters__multiselect-dropdown">
                                {categories.map((cat) => (
                                    <label key={cat.id} className="filters__checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selectedCategories.includes(parseInt(cat.id))}
                                            onChange={() => handleCategoryToggle(parseInt(cat.id))}
                                        />
                                        <span>{cat.name}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="filters__field">
                    <label>{t("filters.place")}</label>
                    <div className="filters__multiselect" ref={placesRef}>
                        <div className="filters__multiselect-header" onClick={() => setPlacesOpen(!placesOpen)}>
                            <span>
                                {selectedPlaces.length > 0
                                    ? t("filters.selected", { count: selectedPlaces.length })
                                    : t("filters.placesPlaceholder")}
                            </span>
                            <span className={`arrow ${placesOpen ? "open" : ""}`}>▼</span>
                        </div>
                        {placesOpen && (
                            <div className="filters__multiselect-dropdown">
                                {places.map((place) => (
                                    <label key={place.id} className="filters__checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selectedPlaces.includes(parseInt(place.id))}
                                            onChange={() => handlePlaceToggle(parseInt(place.id))}
                                        />
                                        <span>{place.name}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="filters__actions">
                <button className="filters__btn filters__btn--apply" onClick={handleApplyFilters}>
                    {t("filters.apply")}
                </button>
                {hasFilters && (
                    <button className="filters__btn filters__btn--reset" onClick={handleResetFilters}>
                        {t("filters.reset")}
                    </button>
                )}
            </div>
        </div>
    );
}
